import { ArrowUpRight, InstagramLogo, FacebookLogo, GoogleLogo } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { LEGAL_LINKS } from "@/components/legal/LegalLayout";
import { openCookieSettings } from "@/lib/consent";

const SOCIAL_LINKS = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/webjesters/",
    Icon: InstagramLogo,
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61590487411718",
    Icon: FacebookLogo,
  },
  {
    label: "Reviews",
    href: "https://share.google/xWSzLXVo1ST2fSjyU",
    Icon: GoogleLogo,
  },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative pt-12 sm:pt-16 pb-28 md:pb-16 border-t border-white/[0.06] bg-[#0a0a0a]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        <div className="grid md:grid-cols-12 gap-9 md:gap-10">
          <div className="md:col-span-6">
            <div className="flex items-center">
              <img
                src="/logo-horizontal-white.png"
                alt="WebJesters"
                width="1600"
                height="333"
                className="h-8 w-auto"
              />
            </div>
            <p className="mt-5 max-w-md text-zinc-300 text-[14.5px] leading-relaxed">
              Managed websites, Google Business Profile setup, Local Visibility
              posting, website improvements, and quote &amp; call automations for
              local service businesses.
            </p>

            {/* Social row (visible on md+; mobile sees them in nav drawer + footer column below) */}
            <div className="mt-7 flex items-center gap-2.5">
              {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/10 bg-white/[0.03] text-zinc-300 hover:text-white hover:bg-brand-700/25 hover:border-brand-500/50 transition-colors"
                >
                  <Icon size={16} weight="regular" />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11.5px] tracking-[0.2em] uppercase text-zinc-400">
              Site
            </div>
            <ul className="mt-4 grid grid-cols-2 md:grid-cols-1 gap-x-5 gap-y-3 text-[13.5px] sm:text-[14px] text-zinc-300">
              {[
                ["Work", "/#work"],
                ["Services", "/#services"],
                ["Pricing", "/#pricing"],
                ["Google Business Profile setup", "/google-business-profile"],
                ["Local Visibility", "/local-visibility"],
                ["Request a free website review", "/website-review"],
                ["Quote & call automations", "/#automations"],
                ["Websites for auto detailing", "/websites-for/auto-detailing"],
                ["Websites for scrap yards", "/websites-for/scrap-metal-recycling"],
                ["FAQ", "/#faq"],
                ["Contact", "/#contact"],
                ["Client login", process.env.REACT_APP_PORTAL_URL || "https://portal.webjesters.com"],
              ].map(([label, href]) => (
                <li key={href}>
                  {href.startsWith("http") ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-white transition-colors"
                    >
                      {label}
                    </a>
                  ) : (
                    <Link to={href} className="hover:text-white transition-colors">
                      {label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11.5px] tracking-[0.2em] uppercase text-zinc-400">
              Reach Us
            </div>
            <ul className="mt-4 space-y-3 text-[14px] text-zinc-300">
              <li>
                <a
                  href="tel:6023905623"
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                >
                  602-390-5623
                  <ArrowUpRight size={13} weight="bold" />
                </a>
              </li>
              <li>
                <a
                  href="mailto:team@webjesters.com"
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                >
                  team@webjesters.com
                  <ArrowUpRight size={13} weight="bold" />
                </a>
              </li>
              <li className="text-zinc-400 text-[13.5px]">
                Mon–Fri · 9am–5pm Arizona time
              </li>
              <li className="text-zinc-400 text-[13.5px]">
                Replies within one business day
              </li>
              <li className="text-zinc-400 text-[13.5px]">Serving local businesses, anywhere.</li>
            </ul>
          </div>
        </div>

        <div className="mt-10 sm:mt-14 pt-6 border-t border-white/[0.06] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[12.5px] text-zinc-400">
          <div>© {new Date().getFullYear()} WebJesters LLC. All rights reserved.</div>
          <nav aria-label="Legal" className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {LEGAL_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`footer-${l.testid}-link`}
                className="hover:text-zinc-300 transition-colors"
              >
                {l.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={openCookieSettings}
              data-testid="footer-do-not-sell"
              className="hover:text-zinc-300 transition-colors text-left"
            >
              Do Not Sell or Share My Personal Information
            </button>
          </nav>
        </div>
      </div>
    </footer>
  );
}
